import React, {ChangeEvent, useCallback, useState} from "react";
import {Button} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {useVisitReportStore} from "../../api/VisitReportStore";
import {ProgressDialog} from "../../components/web-socket/ProgressDialog";
import {Lifetime} from "../../util/Lifetime";
import {ProgressMessage} from "../../api/Model";
import {AsyncChannel} from "../../util/concurrency/AsyncChannel";
import {apiErrorMessage} from "../../util/ApiErrorMessage";

interface UploadButtonProps {
    accept: string;
}

export function UploadButton({accept}: UploadButtonProps) {
    const {uploadReports} = useVisitReportStore();
    const [process, setProcess] = useState<{
        lifetime: Lifetime,
        progress: AsyncGenerator<ProgressMessage, void>,
    }>();

    const handleFileInputChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = '';
        if (files.length > 0) {
            const lifetime = new Lifetime();
            const channel = new AsyncChannel<ProgressMessage>(lifetime);
            setProcess({lifetime, progress: channel.incoming()});

            try {
                await uploadReports(files, lifetime.nested(), progress => {
                    if (lifetime.isAlive) {
                        channel.send({
                            message: `Uploading visit reports (${Math.round(progress)}%)`,
                            progress,
                        });
                    }
                });
                if (lifetime.isAlive) {
                    channel.send({type: 'success', message: 'Uploaded visit reports'});
                }
            } catch (error) {
                if (lifetime.isAlive) {
                    channel.send({
                        type: 'error',
                        message: apiErrorMessage(error) || 'Failed to upload visit reports. Please try again later.',
                    });
                    console.error(error);
                }
            } finally {
                lifetime.terminate();
                setProcess(undefined);
            }
        }
    }, [uploadReports]);

    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                component="label"
                startIcon={<UploadFileIcon/>}
                disabled={process !== undefined}
            >
                Upload
                <input
                    type="file"
                    accept={accept}
                    multiple
                    hidden
                    onChange={handleFileInputChange}
                />
            </Button>
            <ProgressDialog initialMessage="Uploading visit reports" process={process}/>
        </>
    );
}
